import React, { useEffect } from 'react';

import { AdExchangeComponent } from '@/features/advertisement';
import useUnifiedAdBanner, { UnifiedAdType } from '@/hooks/advertisement/useUnifiedAdBanner';
import { AdPageType } from '@/models/ad/list';
import { Client } from '@/types/app';
import { cn } from '@/utils/style';
import AdropBanner from './AdropBanner';
import { Skeleton } from './Skeleton';

interface UnifiedAdBannerProps {
  // 필수 파라미터
  adPageType: AdPageType;
  client: Client;
  contextId?: string;

  // Fallback 설정
  fallbackContent: React.ReactNode;

  // 스타일링
  className?: string;
  borderRadius?: number;
  showAdBadge?: boolean;

  enabled?: boolean;

  // 이벤트 핸들러
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

const UnifiedAdBanner: React.FC<UnifiedAdBannerProps> = (props) => {
  const enabled = props.enabled ?? true;

  const { selectedAd, isLoading, adropParamsFactory, handleAdExchangeExposureLog, adControlUpdatedAt } =
    useUnifiedAdBanner(props.adPageType, props.client, enabled, props.contextId, 'ko_KR');

  const aspect = `aspect-[${adropParamsFactory.getAspect()}]`;
  const rounded = `rounded-[${props.borderRadius}px]`;
  const showAdBadge = props.showAdBadge ?? true;

  useEffect(() => {
    if (selectedAd.type) {
      props.onLoad?.();
    }
  }, [selectedAd]);

  // 로딩 상태
  if (isLoading) {
    return (
      <div className={cn('overflow-hidden', props.className)}>
        <Skeleton
          style={{
            aspectRatio: adropParamsFactory.getAspect(),
            borderRadius: props.borderRadius,
            position: 'relative',
          }}
          aria-label='광고 로딩 중'
        />
      </div>
    );
  }

  // 에드컨트롤 광고 렌더링
  if (selectedAd.type === UnifiedAdType.AdControl) {
    return (
      <AdropBanner
        adData={selectedAd.data}
        adropParamsFactory={adropParamsFactory}
        adControlUpdatedAt={adControlUpdatedAt}
        className={props.className}
        borderRadius={props.borderRadius}
        showAdBadge={showAdBadge}
        fallback={props.fallbackContent}
        onLoad={props.onLoad}
        onError={props.onError}
      />
    );
  }

  // 내부광고 렌더링
  if (selectedAd.type === UnifiedAdType.AdExchange) {
    return (
      <AdExchangeComponent
        bannerAdExchange={selectedAd.data}
        onImpressionLog={handleAdExchangeExposureLog}
        fallback={props.fallbackContent}
      >
        <AdExchangeComponent.Content className={cn(props.className)}>
          <AdExchangeComponent.ImageAd
            className={cn('w-full object-contain', aspect, rounded)}
            isShowAdBadge={showAdBadge}
          />
        </AdExchangeComponent.Content>
      </AdExchangeComponent>
    );
  }

  return props.fallbackContent;
};

export default UnifiedAdBanner;
